import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError, imgUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Save, FileText, Trash2, Plus, X, Wand2, Shirt, Pencil, Copy, Calculator, CheckCircle2, XCircle, Send, RotateCcw, Rocket, ShieldCheck, Eye } from "lucide-react";
import FlatEditor from "../components/FlatEditor";

const VIZ_STATUS = {
  generated: "bg-blue-100 text-blue-800",
  pending: "bg-amber-100 text-amber-800",
};

const SIZE_TEMPLATES = {
  ws_small: { label: "Womenswear XS–XL", sizes: ["XS", "S", "M", "L", "XL"] },
  ws_plus: { label: "Womenswear Plus XL–4XL", sizes: ["XL", "XXL", "3XL", "4XL"] },
  ms: { label: "Menswear S–XXL", sizes: ["S", "M", "L", "XL", "XXL"] },
  eu_num: { label: "EU numeric 34–46", sizes: ["34", "36", "38", "40", "42", "44", "46"] },
  us_num: { label: "US numeric 0–12", sizes: ["0", "2", "4", "6", "8", "10", "12"] },
};

const STATUS_OPTIONS = ["DRAFT", "IN REVIEW", "APPROVED", "PRODUCTION"];

const Field = ({ label, children, testid }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-medium">{label}</label>
    <div className="mt-1" data-testid={testid}>{children}</div>
  </div>
);

const Input = (props) => (
  <input {...props} className={"w-full border border-zinc-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 " + (props.className || "")} />
);

const TextArea = (props) => (
  <textarea {...props} className={"w-full border border-zinc-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 " + (props.className || "")} />
);

export default function TechpackDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tp, setTp] = useState(null);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [tab, setTab] = useState("properties");
  const [generatingFlats, setGeneratingFlats] = useState(false);
  const [editingFlat, setEditingFlat] = useState(null); // "front" | "back" | null
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectComment, setRejectComment] = useState("");
  const [approvalInflight, setApprovalInflight] = useState("");
  const [vizRecords, setVizRecords] = useState([]);
  const [generatingViz, setGeneratingViz] = useState(false);

  useEffect(() => {
    api.get(`/techpacks/${id}`).then((r) => setTp(r.data)).catch(() => navigate("/techpacks"));
    api.get(`/visualization-records?techpack_id=${id}`).then((r) => setVizRecords(r.data || [])).catch(() => {});
  }, [id, navigate]);

  if (!tp) return <div className="p-10 text-sm text-zinc-400">Loading…</div>;
  const canEdit = user?.role === "admin" || user?.id === tp.owner_id;

  const set = (patch) => setTp({ ...tp, ...patch });

  const save = async (extra = {}) => {
    if (!canEdit) return;
    setSaving(true);
    try {
      const payload = {
        style_number: tp.style_number, style_name: tp.style_name, season: tp.season, brand: tp.brand,
        product_line: tp.product_line, category: tp.category, gender: tp.gender,
        description: tp.description, silhouette_notes: tp.silhouette_notes, process_status: tp.process_status,
        modelist: tp.modelist, size_template: tp.size_template, base_size: tp.base_size,
        colorways: tp.colorways, bom: tp.bom, poms: tp.poms, workmanship: tp.workmanship,
        care_instructions: tp.care_instructions, packaging: tp.packaging, hangtag: tp.hangtag,
        comments: tp.comments, ...extra,
      };
      const { data } = await api.patch(`/techpacks/${id}`, payload);
      setTp(data);
      toast.success("Techpack saved");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const changeSizeTemplate = async (template) => {
    const nextBase = SIZE_TEMPLATES[template].sizes.includes(tp.base_size) ? tp.base_size
      : SIZE_TEMPLATES[template].sizes[Math.floor(SIZE_TEMPLATES[template].sizes.length / 2)];
    setTp({ ...tp, size_template: template, sizes: SIZE_TEMPLATES[template].sizes, base_size: nextBase });
    // Persist immediately so backend re-grades POMs
    setSaving(true);
    try {
      const { data } = await api.patch(`/techpacks/${id}`, { size_template: template, base_size: nextBase });
      setTp(data);
      toast.success(`Switched to ${SIZE_TEMPLATES[template].label}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/techpacks/${id}/export/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${tp.style_number || "techpack"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF exported");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setExporting(false);
    }
  };

  const del = async () => {
    if (!window.confirm("Delete this techpack?")) return;
    await api.delete(`/techpacks/${id}`);
    navigate("/techpacks");
  };

  const copyTechpack = async () => {
    try {
      const { data } = await api.post(`/techpacks/${id}/copy`);
      toast.success("Techpack duplicated");
      navigate(`/techpacks/${data.id}`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const createCostSheet = async () => {
    try {
      // If a cost sheet already exists for this techpack, jump to the most recent one
      const existing = await api.get(`/costs/by-techpack/${id}`);
      if (existing.data.length > 0) {
        navigate(`/costing/${existing.data[0].id}`);
        return;
      }
      const { data } = await api.post(`/costs`, { techpack_id: id });
      toast.success("Cost sheet created");
      navigate(`/costing/${data.id}`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const runApproval = async (action, comment = "") => {
    setApprovalInflight(action);
    try {
      const { data } = await api.post(`/techpacks/${id}/approval`, { action, comment });
      setTp(data);
      const map = { submit: "Submitted for review", approve: "Approved", reject: "Rejected", revert_to_draft: "Reverted to draft", move_to_production: "Moved to production" };
      toast.success(map[action] || "Done");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setApprovalInflight("");
    }
  };

  const confirmReject = async () => {
    if (!rejectComment.trim()) {
      toast.error("A comment is required to reject");
      return;
    }
    await runApproval("reject", rejectComment);
    setRejectOpen(false);
    setRejectComment("");
  };

  const generateFlats = async () => {
    if (!canEdit) return;
    setGeneratingFlats(true);
    try {
      const { data } = await api.post(`/techpacks/${id}/generate-flats`);
      setTp(data);
      toast.success("Technical flats generated");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setGeneratingFlats(false);
    }
  };

  const updateColorway = (i, patch) => {
    const cw = [...tp.colorways];
    cw[i] = { ...cw[i], ...patch };
    set({ colorways: cw });
  };
  const removeColorway = (i) => set({ colorways: tp.colorways.filter((_, x) => x !== i) });
  const addColorway = () => set({ colorways: [...tp.colorways, { name: "New color", hex: "#cccccc", code: "" }] });

  const updateBom = (i, patch) => {
    const bom = [...tp.bom];
    bom[i] = { ...bom[i], ...patch };
    set({ bom });
  };
  const removeBom = (i) => set({ bom: tp.bom.filter((_, x) => x !== i) });
  const addBom = () => set({ bom: [...tp.bom, { placement: "", product: "", composition: "", notes: "", is_main: false }] });

  const updatePom = (i, patch) => {
    const poms = [...tp.poms];
    poms[i] = { ...poms[i], ...patch };
    set({ poms });
  };
  const updatePomMeasurement = (i, size, value) => {
    const poms = [...tp.poms];
    poms[i] = { ...poms[i], measurements: { ...poms[i].measurements, [size]: parseFloat(value) || 0 } };
    set({ poms });
  };
  const removePom = (i) => set({ poms: tp.poms.filter((_, x) => x !== i) });
  const addPom = () => {
    const measurements = Object.fromEntries(tp.sizes.map((s) => [s, 0]));
    set({ poms: [...tp.poms, { id: crypto.randomUUID(), code: "NEW", description: "New measurement", comment: "", unit: "CM", inspection: true, tol_plus: 0.5, tol_minus: -0.5, measurements }] });
  };

  const updateWorkmanship = (i, patch) => {
    const w = [...tp.workmanship];
    w[i] = { ...w[i], ...patch };
    set({ workmanship: w });
  };
  const removeWorkmanship = (i) => set({ workmanship: tp.workmanship.filter((_, x) => x !== i) });
  const addWorkmanship = () => set({ workmanship: [...tp.workmanship, { section: "New section", notes: "" }] });

  return (
    <div className="p-10 max-w-6xl mx-auto">
      <button data-testid="techpack-back-button" onClick={() => navigate("/techpacks")} className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-950 transition-colors">
        <ArrowLeft size={14} /> Techpacks
      </button>

      <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">Techpack · {tp.style_number}</p>
          <h1 className="font-heading text-3xl font-bold mt-1 tracking-tight truncate" data-testid="techpack-title">{tp.style_name}</h1>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <span data-testid="techpack-status-pill" className={
              "inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.15em] font-medium px-2.5 py-1 rounded-full " +
              (tp.process_status === "APPROVED" ? "bg-emerald-100 text-emerald-800" :
               tp.process_status === "IN REVIEW" ? "bg-amber-100 text-amber-800" :
               tp.process_status === "PRODUCTION" ? "bg-blue-100 text-blue-800" :
               "bg-zinc-100 text-zinc-700")
            }>
              <ShieldCheck size={11} /> {tp.process_status}
            </span>
            <p className="text-sm text-zinc-500">By {tp.owner_name} · updated {new Date(tp.updated_at || tp.created_at).toLocaleDateString()}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="techpack-save-button" onClick={() => save()} disabled={!canEdit || saving}
            className="inline-flex items-center gap-2 bg-zinc-950 text-white px-4 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
            <Save size={14} /> {saving ? "Saving…" : "Save"}
          </button>
          <button data-testid="techpack-export-button" onClick={exportPdf} disabled={exporting}
            className="inline-flex items-center gap-2 border border-zinc-300 px-4 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-100 transition-colors disabled:opacity-40">
            <FileText size={14} /> {exporting ? "Exporting…" : "Export PDF"}
          </button>
          <button data-testid="techpack-copy-button" onClick={copyTechpack} title="Duplicate this techpack"
            className="inline-flex items-center gap-2 border border-zinc-300 px-4 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-100 transition-colors">
            <Copy size={14} /> Copy
          </button>
          <button data-testid="techpack-cost-button" onClick={createCostSheet} title="Open or create the cost sheet for this techpack"
            className="inline-flex items-center gap-2 border border-zinc-300 px-4 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-100 transition-colors">
            <Calculator size={14} /> Cost sheet
          </button>
          {canEdit && (
            <button data-testid="techpack-delete-button" onClick={del} className="p-2.5 border border-zinc-300 rounded-md hover:bg-red-50 hover:border-red-300 hover:text-red-600 transition-colors"><Trash2 size={14} /></button>
          )}
        </div>
      </div>

      {(() => {
        const isOwner = user?.id === tp.owner_id;
        const isAdmin = user?.role === "admin";
        const status = tp.process_status;
        const actions = [];
        if (status === "DRAFT" && (isOwner || isAdmin)) {
          actions.push({ key: "submit", label: "Submit for review", icon: Send, cls: "bg-zinc-950 text-white hover:bg-zinc-800" });
        }
        if (status === "IN REVIEW" && isAdmin) {
          actions.push({ key: "approve", label: "Approve", icon: CheckCircle2, cls: "bg-emerald-600 text-white hover:bg-emerald-700" });
          actions.push({ key: "reject", label: "Reject", icon: XCircle, cls: "border border-red-300 text-red-700 hover:bg-red-50", onClick: () => setRejectOpen(true) });
        }
        if (status === "APPROVED" && (isOwner || isAdmin)) {
          actions.push({ key: "move_to_production", label: "Move to production", icon: Rocket, cls: "bg-blue-600 text-white hover:bg-blue-700" });
        }
        if (["APPROVED", "PRODUCTION", "IN REVIEW"].includes(status) && (isOwner || isAdmin)) {
          actions.push({ key: "revert_to_draft", label: "Revert to draft", icon: RotateCcw, cls: "border border-zinc-300 text-zinc-700 hover:bg-zinc-100" });
        }
        if (actions.length === 0) return null;
        return (
          <div className="mt-4 flex items-center gap-2 flex-wrap p-3 border border-zinc-200 rounded-lg bg-white" data-testid="signoff-actions">
            <p className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-medium mr-2">Sign-off</p>
            {actions.map(({ key, label, icon: Ic, cls, onClick }) => (
              <button key={key} data-testid={`signoff-${key}`}
                onClick={onClick || (() => runApproval(key))}
                disabled={!!approvalInflight}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors disabled:opacity-40 ${cls}`}>
                <Ic size={12} /> {approvalInflight === key ? "…" : label}
              </button>
            ))}
          </div>
        );
      })()}

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1">
          <img src={imgUrl(tp.cover_image)} alt={tp.style_name} className="w-full rounded-lg border border-zinc-200 bg-white object-contain" data-testid="techpack-cover" />
          <div className="mt-4 p-4 bg-white border border-zinc-200 rounded-lg text-xs text-zinc-500 flex items-start gap-2">
            <Wand2 size={14} className="text-zinc-400 shrink-0 mt-0.5" />
            <div>
              AI drafted every field below from this render. Adjust anything before exporting; measurements automatically re-grade when you change the base size.
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-6">
          <div className="flex gap-1 border-b border-zinc-200 overflow-x-auto">
            {[
              ["properties", "Properties"],
              ["flats", "Technical Flats"],
              ["colorways", "Colorways"],
              ["bom", "BOM"],
              ["poms", "Sizes / POM"],
              ["workmanship", "Workmanship"],
              ["care", "Care & Packaging"],
              ["signoff", "Sign-off"],
              ["visualizations", "Visualizations"],
            ].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`techpack-tab-${k}`}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${tab === k ? "border-zinc-950 text-zinc-950" : "border-transparent text-zinc-500 hover:text-zinc-950"}`}>
                {l}
              </button>
            ))}
          </div>

          {tab === "properties" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="techpack-properties">
              <Field label="Style Name"><Input data-testid="tp-style-name" value={tp.style_name} onChange={(e) => set({ style_name: e.target.value })} /></Field>
              <Field label="Style Number"><Input data-testid="tp-style-number" value={tp.style_number} onChange={(e) => set({ style_number: e.target.value })} /></Field>
              <Field label="Brand"><Input value={tp.brand} onChange={(e) => set({ brand: e.target.value })} /></Field>
              <Field label="Season"><Input value={tp.season} onChange={(e) => set({ season: e.target.value })} /></Field>
              <Field label="Product Line"><Input value={tp.product_line} onChange={(e) => set({ product_line: e.target.value })} /></Field>
              <Field label="Category"><Input value={tp.category} onChange={(e) => set({ category: e.target.value })} /></Field>
              <Field label="Gender"><Input value={tp.gender} onChange={(e) => set({ gender: e.target.value })} /></Field>
              <Field label="Modelist"><Input value={tp.modelist} onChange={(e) => set({ modelist: e.target.value })} /></Field>
              <Field label="Process Status">
                <select data-testid="tp-status" value={tp.process_status} disabled
                  className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm bg-zinc-50 text-zinc-600 cursor-not-allowed focus:outline-none">
                  {STATUS_OPTIONS.map((s) => <option key={s}>{s}</option>)}
                </select>
                <p className="text-[11px] text-zinc-400 mt-1">Managed by the Sign-off actions above.</p>
              </Field>
              <div className="md:col-span-2">
                <Field label="Description">
                  <TextArea data-testid="tp-description" rows={3} value={tp.description} onChange={(e) => set({ description: e.target.value })} />
                </Field>
              </div>
              <div className="md:col-span-2">
                <Field label="Silhouette Notes">
                  <TextArea rows={4} value={tp.silhouette_notes} onChange={(e) => set({ silhouette_notes: e.target.value })} />
                </Field>
              </div>
            </div>
          )}

          {tab === "flats" && (
            <div className="space-y-4" data-testid="techpack-flats">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-medium">AI Technical Flats</p>
                  <p className="text-xs text-zinc-500 mt-1">Clean line-art front and back CADs generated from your product render. Regenerate anytime; the current pair is used in the PDF.</p>
                </div>
                {canEdit && (
                  <button data-testid="techpack-generate-flats" onClick={generateFlats} disabled={generatingFlats}
                    className="inline-flex items-center gap-2 bg-zinc-950 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40 shrink-0">
                    <Shirt size={14} /> {generatingFlats ? "Drawing…" : (tp.flat_front || tp.flat_back ? "Regenerate" : "Generate flats")}
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[["flat_front", "FRONT", "front", "flat_front_callouts"], ["flat_back", "BACK", "back", "flat_back_callouts"]].map(([key, label, which, callKey]) => (
                  <div key={key} className="border border-zinc-200 rounded-lg bg-white p-3" data-testid={`techpack-flat-${key}`}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-medium">{label}{(tp[callKey]?.length || 0) > 0 && <span className="ml-2 text-zinc-400 normal-case tracking-normal">{tp[callKey].length} callout{tp[callKey].length !== 1 ? "s" : ""}</span>}</p>
                      {tp[key] && canEdit && (
                        <button data-testid={`techpack-flat-edit-${which}`} onClick={() => setEditingFlat(which)}
                          className="inline-flex items-center gap-1 text-[11px] text-zinc-500 hover:text-zinc-950 border border-zinc-200 hover:border-zinc-950 rounded-md px-2 py-1 transition-colors">
                          <Pencil size={11} /> Edit
                        </button>
                      )}
                    </div>
                    {tp[key] ? (
                      <div className="relative w-full aspect-square bg-white">
                        <img src={imgUrl(tp[key])} alt={label} className="w-full h-full object-contain" />
                        <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
                          {(tp[callKey] || []).map((co) => {
                            const lx = co.label_x ?? co.x;
                            const ly = co.label_y ?? co.y;
                            return (
                              <line key={`${co.id}-line`} x1={co.x * 100} y1={co.y * 100} x2={lx * 100} y2={ly * 100}
                                stroke="#111111" strokeWidth="0.3" strokeDasharray="1 1" vectorEffect="non-scaling-stroke" />
                            );
                          })}
                        </svg>
                        {(tp[callKey] || []).map((co, i) => {
                          const pom = tp.poms.find((p) => p.id === co.pom_id) || tp.poms.find((p) => p.code === co.pom_code);
                          const val = pom?.measurements?.[tp.base_size];
                          const lx = co.label_x ?? co.x;
                          const ly = co.label_y ?? co.y;
                          return (
                            <div key={co.id}>
                              <div data-testid={`flat-callout-marker-${which}-${i}`}
                                className="absolute -translate-x-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-zinc-950 text-white text-[9px] font-bold flex items-center justify-center border-2 border-white shadow"
                                style={{ left: `${co.x * 100}%`, top: `${co.y * 100}%` }}>
                                {i + 1}
                              </div>
                              <div className="absolute -translate-x-1/2 -translate-y-1/2 bg-white border border-zinc-950 rounded-full px-2 py-0.5 text-[9px] font-bold whitespace-nowrap shadow-sm"
                                style={{ left: `${lx * 100}%`, top: `${ly * 100}%` }}>
                                {co.pom_code}{val != null ? ` ${val}cm` : ""}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="w-full aspect-square flex items-center justify-center border-2 border-dashed border-zinc-200 rounded text-zinc-400 text-xs text-center px-4">
                        {generatingFlats ? "Drawing…" : "Not generated yet. Click Generate flats to have AI draw a technical CAD from your render."}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === "colorways" && (
            <div className="space-y-3" data-testid="techpack-colorways">
              {tp.colorways.map((cw, i) => (
                <div key={i} className="grid grid-cols-1 md:grid-cols-[60px_1fr_1fr_1fr_auto] gap-3 items-center border border-zinc-200 rounded-lg p-3 bg-white" data-testid={`tp-colorway-${i}`}>
                  <input type="color" value={cw.hex || "#cccccc"} onChange={(e) => updateColorway(i, { hex: e.target.value })} className="w-full h-10 rounded cursor-pointer" />
                  <Input placeholder="Name" value={cw.name || ""} onChange={(e) => updateColorway(i, { name: e.target.value })} />
                  <Input placeholder="Hex" value={cw.hex || ""} onChange={(e) => updateColorway(i, { hex: e.target.value })} />
                  <Input placeholder="Code (Pantone)" value={cw.code || ""} onChange={(e) => updateColorway(i, { code: e.target.value })} />
                  <button onClick={() => removeColorway(i)} data-testid={`tp-colorway-remove-${i}`} className="p-2 text-zinc-400 hover:text-red-600"><X size={14} /></button>
                </div>
              ))}
              <button data-testid="tp-colorway-add" onClick={addColorway} className="inline-flex items-center gap-2 text-sm border border-dashed border-zinc-300 rounded-md px-4 py-2 hover:bg-zinc-100"><Plus size={14} /> Add colorway</button>
            </div>
          )}

          {tab === "bom" && (
            <div className="space-y-3" data-testid="techpack-bom">
              {tp.bom.map((b, i) => (
                <div key={i} className="border border-zinc-200 rounded-lg p-3 bg-white space-y-2" data-testid={`tp-bom-${i}`}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Input placeholder="Placement (e.g. Shell)" value={b.placement || ""} onChange={(e) => updateBom(i, { placement: e.target.value })} />
                    <Input placeholder="Product ID" value={b.product || ""} onChange={(e) => updateBom(i, { product: e.target.value })} />
                    <Input placeholder="Composition" value={b.composition || ""} onChange={(e) => updateBom(i, { composition: e.target.value })} />
                    <Input placeholder="Notes" value={b.notes || ""} onChange={(e) => updateBom(i, { notes: e.target.value })} />
                  </div>
                  <div className="flex items-center justify-between">
                    <label className="inline-flex items-center gap-2 text-xs text-zinc-500">
                      <input type="checkbox" checked={!!b.is_main} onChange={(e) => updateBom(i, { is_main: e.target.checked })} />
                      Main material
                    </label>
                    <button onClick={() => removeBom(i)} data-testid={`tp-bom-remove-${i}`} className="text-zinc-400 hover:text-red-600 text-xs"><X size={14} /></button>
                  </div>
                </div>
              ))}
              <button data-testid="tp-bom-add" onClick={addBom} className="inline-flex items-center gap-2 text-sm border border-dashed border-zinc-300 rounded-md px-4 py-2 hover:bg-zinc-100"><Plus size={14} /> Add BOM item</button>
            </div>
          )}

          {tab === "poms" && (
            <div className="space-y-4" data-testid="techpack-poms">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Size Range">
                  <select data-testid="tp-size-template" value={tp.size_template} onChange={(e) => changeSizeTemplate(e.target.value)}
                    className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                    {Object.entries(SIZE_TEMPLATES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </Field>
                <Field label="Base Size">
                  <select data-testid="tp-base-size" value={tp.base_size} onChange={(e) => set({ base_size: e.target.value })}
                    className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                    {tp.sizes.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
              </div>
              <div className="overflow-x-auto border border-zinc-200 rounded-lg bg-white">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-zinc-950 text-white">
                      <th className="text-left px-2 py-2 w-16">Code</th>
                      <th className="text-left px-2 py-2">Description</th>
                      <th className="text-center px-2 py-2 w-14">Tol+</th>
                      <th className="text-center px-2 py-2 w-14">Tol-</th>
                      {tp.sizes.map((s) => <th key={s} className="text-center px-2 py-2 w-16">{s}</th>)}
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {tp.poms.map((p, i) => (
                      <tr key={p.id || i} className="border-t border-zinc-100" data-testid={`tp-pom-${i}`}>
                        <td className="px-2 py-1"><input value={p.code} onChange={(e) => updatePom(i, { code: e.target.value })} className="w-full border border-transparent hover:border-zinc-300 rounded px-1 py-1 focus:outline-none focus:ring-1 focus:ring-zinc-950" /></td>
                        <td className="px-2 py-1"><input value={p.description} onChange={(e) => updatePom(i, { description: e.target.value })} className="w-full border border-transparent hover:border-zinc-300 rounded px-1 py-1 focus:outline-none focus:ring-1 focus:ring-zinc-950" /></td>
                        <td className="px-1 py-1"><input type="number" step="0.1" value={p.tol_plus} onChange={(e) => updatePom(i, { tol_plus: parseFloat(e.target.value) || 0 })} className="w-full text-center border border-transparent hover:border-zinc-300 rounded px-1 py-1 focus:outline-none focus:ring-1 focus:ring-zinc-950" /></td>
                        <td className="px-1 py-1"><input type="number" step="0.1" value={p.tol_minus} onChange={(e) => updatePom(i, { tol_minus: parseFloat(e.target.value) || 0 })} className="w-full text-center border border-transparent hover:border-zinc-300 rounded px-1 py-1 focus:outline-none focus:ring-1 focus:ring-zinc-950" /></td>
                        {tp.sizes.map((s) => (
                          <td key={s} className={`px-1 py-1 ${s === tp.base_size ? "bg-amber-50" : ""}`}>
                            <input type="number" step="0.1" value={p.measurements?.[s] ?? 0}
                              onChange={(e) => updatePomMeasurement(i, s, e.target.value)}
                              data-testid={`tp-pom-${i}-${s}`}
                              className="w-full text-center border border-transparent hover:border-zinc-300 rounded px-1 py-1 focus:outline-none focus:ring-1 focus:ring-zinc-950" />
                          </td>
                        ))}
                        <td className="px-1"><button onClick={() => removePom(i)} data-testid={`tp-pom-remove-${i}`} className="text-zinc-400 hover:text-red-600"><X size={12} /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button data-testid="tp-pom-add" onClick={addPom} className="inline-flex items-center gap-2 text-sm border border-dashed border-zinc-300 rounded-md px-4 py-2 hover:bg-zinc-100"><Plus size={14} /> Add measurement</button>
              <p className="text-xs text-zinc-400">All measurements in CM · base size {tp.base_size} highlighted</p>
            </div>
          )}

          {tab === "workmanship" && (
            <div className="space-y-3" data-testid="techpack-workmanship">
              {tp.workmanship.map((w, i) => (
                <div key={i} className="border border-zinc-200 rounded-lg p-3 bg-white space-y-2" data-testid={`tp-workmanship-${i}`}>
                  <Input placeholder="Section (e.g. Neckline)" value={w.section || ""} onChange={(e) => updateWorkmanship(i, { section: e.target.value })} />
                  <TextArea rows={2} placeholder="Construction notes" value={w.notes || ""} onChange={(e) => updateWorkmanship(i, { notes: e.target.value })} />
                  <button onClick={() => removeWorkmanship(i)} data-testid={`tp-workmanship-remove-${i}`} className="text-zinc-400 hover:text-red-600 text-xs"><X size={14} /></button>
                </div>
              ))}
              <button data-testid="tp-workmanship-add" onClick={addWorkmanship} className="inline-flex items-center gap-2 text-sm border border-dashed border-zinc-300 rounded-md px-4 py-2 hover:bg-zinc-100"><Plus size={14} /> Add workmanship note</button>
            </div>
          )}

          {tab === "care" && (
            <div className="space-y-4" data-testid="techpack-care">
              <Field label="Care Instructions"><TextArea data-testid="tp-care" rows={3} value={tp.care_instructions} onChange={(e) => set({ care_instructions: e.target.value })} /></Field>
              <Field label="Packaging"><TextArea data-testid="tp-packaging" rows={3} value={tp.packaging} onChange={(e) => set({ packaging: e.target.value })} /></Field>
              <Field label="Hangtag"><TextArea data-testid="tp-hangtag" rows={3} value={tp.hangtag} onChange={(e) => set({ hangtag: e.target.value })} /></Field>
              <Field label="Additional Comments"><TextArea data-testid="tp-comments" rows={4} value={tp.comments} onChange={(e) => set({ comments: e.target.value })} placeholder="Anything the factory should know…" /></Field>
            </div>
          )}

          {tab === "signoff" && (
            <div className="space-y-4" data-testid="techpack-signoff">
              <div>
                <h2 className="font-heading text-lg font-bold">Approval history</h2>
                <p className="text-xs text-zinc-500">Every submission, approval and rejection is logged here for audit.</p>
              </div>
              {(tp.approvals || []).length === 0 ? (
                <div className="border-2 border-dashed border-zinc-200 rounded-lg p-10 text-center text-zinc-400 text-xs">
                  No sign-off activity yet. Submit this techpack for review to start the workflow.
                </div>
              ) : (
                <ol className="space-y-3" data-testid="signoff-timeline">
                  {[...tp.approvals].reverse().map((a) => {
                    const style = a.action === "approve" ? { icon: CheckCircle2, cls: "text-emerald-700 bg-emerald-100" }
                      : a.action === "reject" ? { icon: XCircle, cls: "text-red-700 bg-red-100" }
                      : a.action === "submit" ? { icon: Send, cls: "text-amber-700 bg-amber-100" }
                      : a.action === "move_to_production" ? { icon: Rocket, cls: "text-blue-700 bg-blue-100" }
                      : { icon: RotateCcw, cls: "text-zinc-700 bg-zinc-100" };
                    const Ic = style.icon;
                    return (
                      <li key={a.id} className="border border-zinc-200 rounded-lg p-3 bg-white flex items-start gap-3" data-testid={`signoff-entry-${a.id}`}>
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${style.cls}`}><Ic size={14} /></span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm">
                            <span className="font-medium">{a.user_name}</span> <span className="text-zinc-500">{a.label}</span>
                            <span className="text-zinc-400"> · {a.from_status} → {a.to_status}</span>
                          </p>
                          {a.comment && <p className="text-xs text-zinc-600 mt-1 italic">&ldquo;{a.comment}&rdquo;</p>}
                          <p className="text-[11px] text-zinc-400 mt-1">{new Date(a.at).toLocaleString()}</p>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          )}

          {tab === "visualizations" && (
            <div className="space-y-4" data-testid="techpack-visualizations">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-medium">AI Visualizations</p>
                  <p className="text-xs text-zinc-500 mt-1">Photorealistic front and back renders generated from the tech pack specification. Each render is versioned and linked to the current tech pack revision.</p>
                </div>
                {canEdit && (
                  <button
                    onClick={async () => {
                      setGeneratingViz(true);
                      try {
                        await api.post(`/techpacks/${id}/generate-visualization`);
                        toast.success("Visualization generated");
                        const r = await api.get(`/visualization-records?techpack_id=${id}`);
                        setVizRecords(r.data || []);
                      } catch (e) {
                        toast.error(apiError(e));
                      } finally {
                        setGeneratingViz(false);
                      }
                    }}
                    disabled={generatingViz || !tp.category || !tp.bom?.length || !tp.colorways?.length}
                    className="inline-flex items-center gap-2 bg-zinc-950 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40 shrink-0">
                    <Wand2 size={14} /> {generatingViz ? "Generating…" : "Generate Visualization"}
                  </button>
                )}
              </div>
              {(!tp.category || !tp.bom?.length || !tp.colorways?.length) && (
                <p className="text-xs text-amber-600">
                  Visualization requires a garment category, at least one BOM material, and at least one colorway.
                  {!tp.category && " Missing: category."}{!tp.bom?.length && " Missing: BOM materials."}{!tp.colorways?.length && " Missing: colorways."}
                </p>
              )}
              {vizRecords.length === 0 ? (
                <div className="border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400">
                  <Eye size={26} className="mx-auto mb-3" />
                  <p className="text-sm">No visualization records yet. Generate one from a qualifying tech pack.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {vizRecords.map((vr) => (
                    <div key={vr.id} className="border border-zinc-200 rounded-lg bg-white p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className={`text-[11px] px-2 py-0.5 rounded font-medium ${VIZ_STATUS[vr.status] || VIZ_STATUS.generated}`}>{vr.status || "generated"}</span>
                          <span className="text-xs text-zinc-500">Version {vr.techpack_version || 1}</span>
                        </div>
                        <span className="text-[11px] text-zinc-400">{vr.created_at ? new Date(vr.created_at).toLocaleString() : ""}</span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {(vr.rendered_images || []).map((img, idx) => (
                          <div key={idx} className="border border-zinc-100 rounded-lg bg-zinc-50 p-2">
                            <img src={img.url?.startsWith("/") ? api.defaults.baseURL?.replace("/api", "") + img.url : img.url} alt={img.label || `Render ${idx + 1}`}
                              className="w-full aspect-square object-contain bg-white rounded" />
                            <p className="text-[11px] text-zinc-500 mt-1 text-center">{img.label || `Render ${idx + 1}`}</p>
                          </div>
                        ))}
                      </div>
                      {vr.prompt && <p className="text-xs text-zinc-400 mt-2 italic">Prompt: {vr.prompt}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <FlatEditor
        open={!!editingFlat}
        onClose={() => setEditingFlat(null)}
        techpackId={id}
        which={editingFlat}
        imageUrl={editingFlat === "front" ? tp.flat_front : tp.flat_back}
        poms={tp.poms || []}
        baseSize={tp.base_size}
        existingCallouts={editingFlat === "front" ? (tp.flat_front_callouts || []) : (tp.flat_back_callouts || [])}
        onSaved={(next) => setTp(next)}
      />
      {rejectOpen && (
        <div className="fixed inset-0 z-50 bg-zinc-950/60 flex items-center justify-center p-4" data-testid="reject-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <XCircle size={18} className="text-red-600" />
                <h2 className="font-heading text-lg font-bold">Reject techpack</h2>
              </div>
              <button onClick={() => setRejectOpen(false)} data-testid="reject-close" className="p-1.5 rounded hover:bg-zinc-100 text-zinc-500"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-xs text-zinc-500">The designer will be notified. Explain what needs to change before this techpack can be approved.</p>
              <textarea data-testid="reject-comment" rows={4} value={rejectComment} onChange={(e) => setRejectComment(e.target.value)}
                placeholder="e.g. Neckline is too wide, needs to be reduced by 1cm; also change the shell composition to 100% cotton"
                className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" autoFocus />
            </div>
            <div className="px-5 py-3 border-t border-zinc-200 flex items-center justify-end gap-2">
              <button onClick={() => setRejectOpen(false)} className="text-sm px-4 py-2 rounded-md hover:bg-zinc-100">Cancel</button>
              <button data-testid="reject-confirm" onClick={confirmReject} disabled={approvalInflight === "reject" || !rejectComment.trim()}
                className="inline-flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-700 disabled:opacity-40">
                <XCircle size={14} /> {approvalInflight === "reject" ? "Rejecting…" : "Reject techpack"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
